import { useState, useEffect } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge, ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, MapPin, Download, Star, Folder } from 'lucide-react'
import { useLocationStore } from '../../../../stores/locationStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedLocation, LocationInput, LocationResult } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { LocationEditModal } from './LocationEditModal'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedLocationsListProps {
  onLoad: (location: { input: LocationInput; result: LocationResult }) => void
  onExport?: () => void
  onImport?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export function SavedLocationsList({ onLoad, onExport, onImport }: SavedLocationsListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [editingLocation, setEditingLocation] = useState<SavedLocation | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { 
    locations, 
    deleteLocation, 
    clearLocations, 
    exportLocations, 
    importLocations,
    updateLocation,
    toggleFavorite 
  } = useLocationStore()
  
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredLocations = locations
  if (selectedFolderId === 'all') {
    folderFilteredLocations = locations
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('location')
    folders.forEach(folder => {
      getItemsInFolder('location', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredLocations = locations.filter(location => !allFolderItemIds.has(location.id))
  } else {
    const itemIds = getItemsInFolder('location', selectedFolderId)
    folderFilteredLocations = locations.filter(location => itemIds.includes(location.id))
  }
  
  const filteredLocations = folderFilteredLocations.filter(location =>
    location.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.result.basicInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.result.basicInfo.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (location.tags && location.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  )

  const handleExportText = () => {
    const text = locations
      .map(loc => {
        const r = loc.result
        return `【${r.basicInfo.name}】
タイプ: ${r.basicInfo.type}
規模: ${r.basicInfo.scale}
危険度: ${r.basicInfo.accessibility?.difficulty || 5}/10
概要: ${r.basicInfo.summary}
説明: ${r.basicInfo.description}
━━━━━━━━━━━━━━━━━━━━━━`
      })
      .join('\n\n')
    
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `locations_${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleExportJSON = () => {
    if (onExport) {
      onExport()
    } else {
      const jsonString = exportLocations()
      const blob = new Blob([jsonString], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `locations_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const handleImportJSON = (jsonString: string) => {
    if (onImport) {
      const file = new File([jsonString], "import.json", { type: "application/json" })
      const event = {
        target: {
          files: [file]
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>
      onImport(event)
    } else {
      try {
        importLocations(jsonString)
      } catch (error) {
        console.error('Import failed:', error)
        alert('インポートに失敗しました。ファイル形式を確認してください。')
      }
    }
  }

  const handleExportSingleText = (location: SavedLocation) => {
    const r = location.result
    const text = `【${r.basicInfo.name}】
タイプ: ${r.basicInfo.type}
規模: ${r.basicInfo.scale}
危険度: ${r.basicInfo.accessibility?.difficulty || 5}/10
概要: ${r.basicInfo.summary}
説明: ${r.basicInfo.description}

■地理・地形
主要地形: ${r.geography.terrain.primary}
標高: ${r.geography.terrain.elevation}
面積: ${r.geography.terrain.area}

■環境・気候
気候: ${r.environment.climate.type}
平均気温: ${r.environment.climate.averageTemperature}
湿度: ${r.environment.climate.humidity}

■生物相
${r.wildlife?.fauna?.common?.map(f => `・${f.name}: ${f.description}`).join('\n') || '情報なし'}`

    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${location.title.replace(/[^\w\s]/gi, '_')}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleExportSingleJSON = (location: SavedLocation) => {
    const jsonString = JSON.stringify(location, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${location.title.replace(/[^\w\s]/gi, '_')}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みロケーション ({locations.length}/100)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したロケーション・自然環境設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="ロケーションを検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
          <ExportImportButtons
            onExportText={handleExportText}
            onExportJSON={handleExportJSON}
            onImportJSON={handleImportJSON}
            dataTypeName="ロケーション"
          />

          {locations.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのロケーションを削除しますか？')) {
                  clearLocations()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="location"
                items={locations}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('location', itemId)
                  moveItemBetweenFolders('location', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* ロケーションリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredLocations.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchTerm ? '検索結果がありません' : '保存済みのロケーションがありません'}
                  </p>
                ) : (
                  filteredLocations.map((location) => (
                    <div 
                      key={location.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(location.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === location.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{location.title}</h3>
                        {location.favorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{location.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">{location.result.basicInfo.scale}</Badge>
                        <Badge variant="destructive" className="text-xs">危険度: {location.result.basicInfo.accessibility?.difficulty || 5}</Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {location.result.basicInfo.summary}
                      </p>
                      
                      {location.tags && location.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {location.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(location.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2 w-48">
                        <FolderMoveSelect
                          generatorType="location"
                          itemId={location.id}
                          currentFolderId={getItemFolder('location', location.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad({ input: location.input, result: location.result })}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingLocation(location)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleFavorite(location.id)}
                        title="お気に入り"
                        className="w-12 h-7 px-1"
                      >
                        <Star className={`h-3 w-3 ${location.favorite ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportSingleText(location)}
                        title="テキスト"
                        className="w-12 h-7 px-1 text-xs"
                      >
                        TXT
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportSingleJSON(location)}
                        title="JSON"
                        className="w-12 h-7 px-1 text-xs"
                      >
                        JSON
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このロケーションを削除しますか？')) {
                            deleteLocation(location.id)
                          }
                        }}
                        title="削除"
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* デスクトップ版: 横並びレイアウト */}
                  <div className="hidden sm:flex sm:justify-between sm:items-start w-full">
                    <div className="flex-grow mr-4 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{location.title}</h3>
                        {location.favorite && <Badge variant="secondary">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="outline">{location.result.basicInfo.type}</Badge>
                        <Badge variant="outline">{location.result.basicInfo.scale}</Badge>
                        <Badge variant="destructive">危険度: {location.result.basicInfo.accessibility?.difficulty || 5}</Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {location.result.basicInfo.summary}
                      </p>
                      
                      {location.tags && location.tags.length > 0 && (
                        <div className="flex gap-1 mb-2 flex-wrap">
                          {location.tags.map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground">
                        {new Date(location.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2 w-48">
                        <FolderMoveSelect
                          generatorType="location"
                          itemId={location.id}
                          currentFolderId={getItemFolder('location', location.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* デスクトップ版: 縦並びボタン */}
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad({ input: location.input, result: location.result })}
                        title="読み込み"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        読込
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingLocation(location)}
                        title="編集"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        編集
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleFavorite(location.id)}
                        title="お気に入り"
                      >
                        <Star className={`h-4 w-4 mr-1 ${location.favorite ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                        お気に入り
                      </Button>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExportSingleText(location)}
                          title="テキストエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          TXT
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExportSingleJSON(location)}
                          title="JSONエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          JSON
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このロケーションを削除しますか？')) {
                            deleteLocation(location.id)
                          }
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        削除
                      </Button>
                    </div>
                  </div>
                </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      {editingLocation && (
        <LocationEditModal
          location={editingLocation}
          onSave={(updates) => {
            updateLocation(editingLocation.id, updates)
            setEditingLocation(null)
          }}
          onClose={() => setEditingLocation(null)}
        />
      )}
    </div>
  )
}